/* eslint-disable @typescript-eslint/no-explicit-any */
import { useDispatch } from "react-redux";
import React, { useEffect, useState } from "react";
import { Typography, Box, Button } from "@mui/material";

import List from "../../../components/List";
import { EmptyView } from "../../../components/OList";
import LeaveApplicationInfo from "./LeaveApplicationInfo";
import PeriodPicker from "../../../components/PeriodPicker";
import { Employee, UserMode } from "../../../interfaces/User";
import { notificationAdd } from "../../../actions/notification";
import { CalendarFunctions } from "../../../components/Calendar";
import { getLeaveApplications } from "../../../api/leaveApplication";
import { LeaveApplicationCreateEdit } from "./LeaveApplicationCreateEdit";
import { LeaveApplicationStatusChip } from "./LeaveApplicationStatusChip";
import { ServiceProvider } from "../../../interfaces/ServiceProvidersInterface";
import { ILeaveApplication } from "../../../interfaces/LeaveApplicationInterface";
import EmployeeAndSpSelect from "../../../components/Employee/EmployeeAndSpSelect";
import { bsFullDate } from "../../../components/Calendar/functions/calendarFunctions";

interface PropTypes {
  showEmployeeFilter?: boolean;
  employeeIds?: number[];
  serviceProviderIds?: number[];
  enableCreateNew?: boolean;
  createLeaveOnBehalfOfEmployee?: boolean;
}

// eslint-disable-next-line import/prefer-default-export
export const LeaveApplicationList = ({
  employeeIds = [],
  serviceProviderIds = [],
  showEmployeeFilter = false,
  enableCreateNew = false,
  createLeaveOnBehalfOfEmployee = true
}: PropTypes): React.JSX.Element => {
  const dispatch = useDispatch();

  const [showCreateEditPanel, setShowCreateEditPanel] = useState(false);
  const [leaveApplications, setLeaveApplications] = useState<ILeaveApplication[]>([]);
  const [selectedLeaveApplication, setSelectedLeaveApplication] =
    useState<ILeaveApplication | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | ServiceProvider | null>(null);

  const handleSuccess = (data?: ILeaveApplication) => {
    if (data) {
      setLeaveApplications((prev) => {
        const index = prev.findIndex((l) => l.id === data.id);
        if (index > -1) {
          const newApps = [...prev];
          newApps[index] = data;
          return newApps;
        }
        return [data, ...prev];
      });
      if (selectedLeaveApplication?.id === data.id) {
        setSelectedLeaveApplication(data);
      }
    }

    setShowCreateEditPanel(false);
  };

  const startOfMonth = CalendarFunctions.startOfMonth(new Date());
  const endOfMonth = CalendarFunctions.endOfMonth(new Date());

  const [filter, setFilter] = useState<{
    employeeIds: number[];
    serviceProviderIds: number[];
    fromDate: string;
    untilDate: string;
  }>({
    employeeIds: employeeIds || [],
    serviceProviderIds: serviceProviderIds || [],
    fromDate: startOfMonth.toISOString(),
    untilDate: endOfMonth.toISOString()
  });

  useEffect(() => {
    (async () => {
      try {
        const { success, data } = await getLeaveApplications(filter);
        if (success) {
          setLeaveApplications(data);
        }
      } catch (err: any) {
        dispatch(
          notificationAdd({
            id: new Date().getMilliseconds(),
            variant: "error",
            message: err?.message || "Error while fetching attendance data",
            autoTimeout: true
          })
        );
      }
    })();
  }, [dispatch, filter]);

  return (
    <Box
      sx={{
        py: "10px",
        flexGrow: 1,
        height: "100%"
      }}
    >
      <Box
        sx={{
          height: "calc(100% - 40px)",
          mt: 2
        }}
      >
        <List<ILeaveApplication>
          automation="leaveApplicationList"
          data={leaveApplications}
          withoutSearch
          isResponsive
          hideCreateButton
          customCreateButton={
            enableCreateNew || createLeaveOnBehalfOfEmployee ? (
              <Button
                variant="contained"
                sx={{ height: 35 }}
                onClick={() => {
                  setShowCreateEditPanel(true);
                  setSelectedLeaveApplication(null);
                }}
              >
                {createLeaveOnBehalfOfEmployee ? "Apply Leave for Employee" : "Apply Leave"}
              </Button>
            ) : (
              <></>
            )
          }
          additionalHeaderFilters={
            <Box
              sx={{
                width: "100%",
                display: "flex",
                gap: "10px",
                alignItems: "center"
              }}
            >
              {showEmployeeFilter && (
                <EmployeeAndSpSelect
                  selectedEmployee={selectedEmployee}
                  onChange={(emp) => {
                    setSelectedEmployee(emp);
                    setFilter((prev) => {
                      if (!emp) {
                        return {
                          ...prev,
                          employeeIds: [],
                          serviceProviderIds: []
                        };
                      }

                      if (emp?.userAuthenticable?.[0]?.authenticable === UserMode.Employee) {
                        return {
                          ...prev,
                          employeeIds: emp ? [emp.id] : [],
                          serviceProviderIds: []
                        };
                      }

                      if (emp?.userAuthenticable?.[0]?.authenticable === UserMode.ServiceProvider) {
                        return {
                          ...prev,
                          employeeIds: [],
                          serviceProviderIds: emp ? [emp.id] : []
                        };
                      }

                      return prev;
                    });
                  }}
                  sx={{ height: 35, width: 200 }}
                />
              )}

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PeriodPicker
                  limitToMonth
                  hideFiscalYear
                  variant="outlined"
                  start={filter.fromDate ? new Date(filter.fromDate) : startOfMonth}
                  end={filter.untilDate ? new Date(filter.untilDate) : endOfMonth}
                  onChange={({ start, end }) => {
                    setFilter({
                      ...filter,
                      fromDate: start.toISOString(),
                      untilDate: end.toISOString()
                    });
                  }}
                  sx={{ height: 35, width: 200 }}
                />
              </Box>
            </Box>
          }
          columns={[
            {
              key: "name",
              label: "Name",
              sortable: true,
              formatter: (row) => {
                let name = "-";
                if (row.employee) {
                  name = `${row.employee.firstName} ${row.employee.lastName}`;
                } else if (row.serviceProvider) {
                  name = `${row.serviceProvider.firstName} ${row.serviceProvider.lastName}`;
                }
                return (
                  <Typography component="span" variant="body2">
                    {name}
                  </Typography>
                );
              }
            },
            {
              key: "type",
              label: "Type",
              sortable: true,
              formatter: (row) => (
                <Typography component="span" variant="body2">
                  {row.isServiceProvider ? "Service Provider" : "Employee"}
                </Typography>
              )
            },
            {
              key: "created_at",
              label: "Applied At",
              // eslint-disable-next-line camelcase
              formatter: ({ created_at }) => (
                <Typography component="span" variant="body2">
                  {/* eslint-disable-next-line camelcase */}
                  {created_at ? bsFullDate(created_at) : "-"}
                </Typography>
              )
            },
            {
              key: "startDate",
              label: "Start Date",
              sortable: true,
              formatter: ({ startDate }) => (
                <Typography component="span" variant="body2">
                  {bsFullDate(startDate)}
                </Typography>
              )
            },
            {
              key: "endDate",
              label: "End Date",
              sortable: true,
              formatter: ({ endDate }) => (
                <Typography component="span" variant="body2">
                  {bsFullDate(endDate)}
                </Typography>
              )
            },
            {
              key: "leaveType",
              label: "Leave Type",
              sortable: true,
              formatter: ({ leaveType }) => (
                <Typography component="span" variant="body2" sx={{ textTransform: "capitalize" }}>
                  {leaveType}
                </Typography>
              )
            },
            {
              key: "status",
              label: "Status",
              sortable: true,
              formatter: ({ status }) => (
                <Typography component="span" variant="body2">
                  <LeaveApplicationStatusChip status={status} />
                </Typography>
              )
            }
          ]}
          activeRow={selectedLeaveApplication?.id}
          onRowClick={(row) => {
            setSelectedLeaveApplication(
              selectedLeaveApplication?.id === row.id ? null : (row as ILeaveApplication)
            );
          }}
          segementSummaryRenderer={(acc) => (
            <Box
              sx={{
                background: "#e6e6e6",
                flexGrow: 1,
                display: "flex",
                gap: "8px",
                padding: "10px 32px 4px 20px",
                fontSize: "14px"
              }}
            >
              <Box component="span">{acc.segment || "Total"}</Box>
              <Box component="span">({acc.rows.length} Records)</Box>
            </Box>
          )}
        >
          <EmptyView>
            <Typography component="div" variant="body2">
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "350px",
                  width: "100%"
                }}
              >
                <Typography component="div">
                  <Box textAlign="center" marginTop="16px">
                    No leave applications found
                  </Box>
                </Typography>
              </Box>
            </Typography>
          </EmptyView>
        </List>
      </Box>

      {selectedLeaveApplication && (
        <LeaveApplicationInfo
          leaveApplication={selectedLeaveApplication}
          setLeaveApplications={setLeaveApplications}
          setSelectedLeaveApplication={setSelectedLeaveApplication}
          onClose={() => setSelectedLeaveApplication(null)}
          onEdit={() => setShowCreateEditPanel(true)}
        />
      )}

      {showCreateEditPanel && (
        <LeaveApplicationCreateEdit
          createLeaveOnBehalfOfEmployee={createLeaveOnBehalfOfEmployee}
          leaveApplication={selectedLeaveApplication}
          onClose={() => {
            setShowCreateEditPanel(false);
            setSelectedLeaveApplication(null);
          }}
          onSuccess={handleSuccess}
        />
      )}
    </Box>
  );
};
